import React, {useEffect, useMemo, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import Cookie from 'coreClasses/cookies/Cookie';
import CookiesMgr from 'coreClasses/cookies/CookiesMgr';
import DeviceMgr from 'coreClasses/device/DeviceMgr';
import AbstractProductModel from 'coreClasses/product/AbstractProductModel';
import {DevToolkit} from 'coreComponents';
import {useCustomerMgr, useLoadRiskifiedScript, useLocalesMgr, useLocation, useLockScroll, useOrdersMgr, usePayrailsFraudSDK, usePreviousUrl, useSignifydFingerprint, useUI} from 'coreHooks';
import {receiveConfigurations} from 'coreStore/data/configurations/index';
import {absoluteUrl, getEncodedURI} from 'coreUtils/url';
import {getParamsFromPath, matchSiteAliasPerID} from 'coreUtils/utils';
import validationRules from 'coreUtils/validationRules';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import Button from 'brandComponents/Button';
import Dropdown from 'brandComponents/Dropdown';
import Field from 'brandComponents/Form/Subcomponents/Field';
import Icon from 'brandComponents/Icon';
import {
	PERSONAL_SHOPPER_COOKIE,
	REGISTRATION_DEVICE_TYPE,
	SG_OPEN_REGISTER_FORM,
	USER_GUID_COOKIE,
	UTM_SOURCE_COOKIE,
} from 'brandConstants';
import {useShopper, useSubscribeSource, useWishlist} from 'brandHooks';

const AppEffects = (props) => {
	const {isToolkitEnabled, isCMSPreviewEnabled} = props;
	const {managerRepository} = useGeneralProvider();
	const location = useLocation();
	const localesMgr = useLocalesMgr();
	const wishlist = useWishlist();
	const customerMgr = useCustomerMgr();
	const ordersMgr = useOrdersMgr();
	const {lockScroll} = useLockScroll({
		lockerClassName: 'requires-login-lock-scroll',
	});

	const [customerGroupId, setCustomerGroupId] = useState(null);
	const srcParam = location.searchParams.get('src');
	const [src, setSrc] = useState(srcParam);
	const [effectiveDateTime, setEffectiveDateTime] = useState(null);

	const utmSource = location.searchParams.get('utmSource');
	const personalShopperUserId =
		location.searchParams.get('personalShopperUserId') || CookiesMgr.getCookie('personalShopperUserId').getValue();

	const isServerUI = useUI('isServer');
	const isLoginFlyoutOpened = useUI('openedFlyoutIds').includes('login');
	const isSizeSelectorOpened = useUI('openedFlyoutIds').includes('sizeSelector');
	const isColorSelectorOpened = useUI('openedFlyoutIds').includes('colorSelector');
	const productToAddtoWishlist = useUI('addProductToWishlist');
	const isHomePage = location.isHomePage;
	const isRequiresLogin = useUI('requiresLoginFeature');

	const currentLocale = localesMgr.getCurrentLocale();
	const currentCountryCode = currentLocale.getCountryCode();
	// Get the current currency to be used throughout the app
	const currency = currentLocale.getCurrencyCode();
	const customer = customerMgr.getCurrentCustomer();

	const context = useMemo(() => {
		const customerMgr = managerRepository.getCustomerMgr();
		const storedContext = customerMgr.getCustomerContext() || {sourceCode: undefined};
		const customQualifiers: Record<string, any> = {
			shippingCountry: currentCountryCode,
			platform: customerMgr.CONTEXT_PLATFORM_WEB,
		};
		const assignmentQualifiers: Record<string, any> = {
			platform: customerMgr.CONTEXT_PLATFORM_WEB,
			shippingCountry: currentCountryCode,
		};
		let contextData;

		if (utmSource) {
			assignmentQualifiers.utmSource = utmSource;
			customQualifiers.utmSource = utmSource;
		}

		if (personalShopperUserId) {
			assignmentQualifiers.personalShopperUserId = personalShopperUserId;
			customQualifiers.personalShopperUserId = personalShopperUserId;
		}

		if (!isServerUI) {
			customQualifiers.deviceType = DeviceMgr.isMobile(navigator?.userAgent)
				? REGISTRATION_DEVICE_TYPE.MOBILE
				: REGISTRATION_DEVICE_TYPE.DESKTOP;
		}

		if (customerGroupId !== undefined) {
			customQualifiers.customergroupId = customerGroupId;
		} else {
			delete customQualifiers.customergroupId;
		}

		contextData = {
			customQualifiers,
			assignmentQualifiers,
		};

		if (effectiveDateTime) {
			contextData.effectiveDateTime = effectiveDateTime;
		} else {
			delete contextData.effectiveDateTime;
		}

		if (src !== undefined) {
			contextData.sourceCode = src;
		} else if (storedContext.sourceCode) {
			contextData.sourceCode = storedContext.sourceCode;
		}

		return contextData;
	}, [currentCountryCode, utmSource, personalShopperUserId, src, effectiveDateTime, customerGroupId]);

	useEffect(() => {
		// needed for analytics to check if customer made transactions
		if (customer?.isRegistered) {
			ordersMgr.fetchNextPage(customer.customerId);
		}
	}, [customer?.isRegistered]);

	useEffect(() => {
		const originalUrl = getEncodedURI(window.location.pathname);
		const {siteRef} = getParamsFromPath(originalUrl);
		const cookiesSiteId = CookiesMgr.getCookie('siteid').getValue();

		if (siteRef || cookiesSiteId) {
			const siteRefSiteAlias = matchSiteAliasPerID(siteRef) || cookiesSiteId;

			if (siteRefSiteAlias) {
				managerRepository.getCommerceApi().setSiteId(siteRefSiteAlias);
				managerRepository.getSfccAPI().setSiteId(siteRefSiteAlias);
				managerRepository
					.getSfccAPI()
					.getConfigurations()
					.then((updatedConfigurations) => {
						managerRepository.getConfigurationsMgr().invalidateAllCache();
						managerRepository.getStoreMgr().getStore()
							.dispatch(receiveConfigurations(updatedConfigurations));
					});
			}
		}
	}, []);

	useSignifydFingerprint();
	usePayrailsFraudSDK();
	useShopper({currency, context, isReady: props.isReady});
	useSubscribeSource();
	useLoadRiskifiedScript();
	usePreviousUrl();

	useEffect(() => {
		const popupsMgr = managerRepository.getPopupsMgr();
		const searchParamsURL: URL = new URL(absoluteUrl(location.href));
		const searchParams: URLSearchParams = new URLSearchParams(searchParamsURL.search);
		const userId: string = searchParams.get('user_id');

		if (userId) {
			const cookieAttributes: Record<string, string | number> = {
				path: PERSONAL_SHOPPER_COOKIE.path,
				expires: PERSONAL_SHOPPER_COOKIE.expiration,
			};

			const cookie: Cookie = new Cookie(PERSONAL_SHOPPER_COOKIE.cookieName, userId, cookieAttributes);
			CookiesMgr.setCookie(cookie);
		}

		popupsMgr.closePopup('bannerPopup');
	}, [location.currentRoute]);

	useEffect(() => {
		const popupsMgr = managerRepository.getPopupsMgr();
		const flyoutMgr = managerRepository.getFlyoutsMgr();

		if (isHomePage) {
			if (sessionStorage.getItem(SG_OPEN_REGISTER_FORM) === 'true') {
				flyoutMgr.openFlyout('login');
			} else {
				flyoutMgr.closeAllFlyouts();
			}
		} else {
			flyoutMgr.closeAllFlyouts();
		}

		sessionStorage.removeItem(SG_OPEN_REGISTER_FORM);

		if (popupsMgr.isPopupOpened('filtersPopup')) {
			popupsMgr.closePopup('filtersPopup');
		}
	}, [location.pathname]);

	useEffect(() => {
		const searchParamsURL: URL = new URL(absoluteUrl(location.href));
		const searchParams: URLSearchParams = new URLSearchParams(searchParamsURL.search);
		const utmSource: string = searchParams.get('utm_source');
		const GUIDCookie: Cookie = CookiesMgr.getCookie('userGuid');

		if (utmSource) {
			const cookieAttributes: Record<string, string | number> = {
				path: UTM_SOURCE_COOKIE.path,
				expires: UTM_SOURCE_COOKIE.expiration,
			};
			const cookie: Cookie = new Cookie(UTM_SOURCE_COOKIE.cookieName, utmSource, cookieAttributes);

			CookiesMgr.setCookie(cookie);
		}

		if (!GUIDCookie.getValue()) {
			const cookieAttributes: Record<string, string | number> = {
				path: USER_GUID_COOKIE.path,
				expires: USER_GUID_COOKIE.expiration,
			};
			const cookie: Cookie = new Cookie(USER_GUID_COOKIE.cookieName, uuidv4(), cookieAttributes);

			CookiesMgr.setCookie(cookie);
		}
	}, []);

	useEffect(() => {
		const productsMgr = managerRepository.getProductsMgr();

		wishlist.items.forEach((item) => {
			if (!item.c_variationGroupId || !validationRules.isVariationGroupId(item.c_variationGroupId)) {
				const product = productsMgr.getProduct(item.productId) as AbstractProductModel;

				wishlist.updateListItem({
					id: item.id,
					productId: item.productId,
					c_variationGroupId: product.getVariationGroupId(null),
					quantity: 1,
				});
			}
		});
	}, [wishlist.items]);

	useEffect(() => {
		if (srcParam && srcParam !== src) {
			setSrc(srcParam);
		}
	}, [srcParam]);

	useEffect(() => {
		const UiMgr = managerRepository.getUiMgr();

		if (!isLoginFlyoutOpened) {
			if (productToAddtoWishlist && !isSizeSelectorOpened && !productToAddtoWishlist.shouldWaitToReset) {
				UiMgr.resetProductToAddToWishlist();
			}

			UiMgr.resetProductToMoveFromBasketToWishlist();
		}

		if (!isSizeSelectorOpened) {
			UiMgr.resetSizeSelectorSelectedPid();
			UiMgr.resetProductToAddToCart();
		}

		if (!isColorSelectorOpened) {
			UiMgr.resetColorSelectorSelectedPid();
		}
	}, [isLoginFlyoutOpened, isSizeSelectorOpened, isColorSelectorOpened]);

	useEffect(() => {
		const isRequiresLoginEnabled = isRequiresLogin?.enabled && customer.isReady() && !customer.isRegistered;

		if (isRequiresLoginEnabled) {
			managerRepository.getFlyoutsMgr().openFlyout('login', true);
		}

		lockScroll(isRequiresLoginEnabled);

		return () => lockScroll(false);
	}, [isRequiresLogin, customer.isReady(), customer.isRegistered]);

	return (
		<>
			{(isToolkitEnabled || isCMSPreviewEnabled) && (
				<DevToolkit
					isCMSPreviewEnabled={isCMSPreviewEnabled}
					isToolkitEnabled={isToolkitEnabled}
					components={{Button, Icon, Field}}
					onToolkitSubmit={(groupId, sourceCode, dateTime) => {
						setCustomerGroupId(groupId);
						setSrc(sourceCode);
						setEffectiveDateTime(dateTime);
					}}
					renderCustomerGroupSelector={({value, options, onSelect, containerRef}) => (
						<Dropdown value={value} label="Select a customer group">
							<div>
								{!value && <span>Customer Group</span>}
								<Dropdown.Trigger customIcon={<Icon name="arrow-down" width={16} height={16} />} />
							</div>
							{containerRef.current && (
								<Dropdown.Menu
									title="Select a customer group."
									searchable
									closable
									data={options}
									onSelect={onSelect}
									iconSearch="search"
									container={containerRef.current}
									placement="top"
								/>
							)}
						</Dropdown>
					)}
				/>
			)}
		</>
	);
};

export default AppEffects;
